import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { BASE_URL, PAYPAL_CLIENT_ID } from "../../config/RequestMethod";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import html2canvas from "html2canvas";
import html2pdf from "html2pdf.js";
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Badge,
  Button,
  VStack,
  HStack,
  Progress,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Image,
  Divider,
  useToast,
  Grid,
  GridItem,
  Icon,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  keyframes,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import {
  FaCheckCircle,
  FaLock,
  FaUser,
  FaBuilding,
  FaMoneyBillWave,
  FaInfoCircle,
  FaFileAlt,
  FaShieldAlt,
  FaDownload,
  FaEye,
  FaUniversity,
  FaChalkboardTeacher,
  FaCalendarAlt,
  FaCreditCard,
} from "react-icons/fa";

// --- Animations ---
const pulse = keyframes`
  0% { transform: scale(0.95); opacity: 0.5; }
  50% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.5; }
`;

const heartbeat = keyframes`
  0% { transform: scale(1); }
  15% { transform: scale(1.1); }
  30% { transform: scale(1); }
  45% { transform: scale(1.1); }
  70% { transform: scale(1); }
`;

// --- Helper: Load Razorpay ---
const loadRazorpayScript = (src: string) => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const AdmissionPaymentPortal = () => {
  const [searchParams] = useSearchParams();
  const admissionId = searchParams.get("admissionId");
  const [admission, setAdmission] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  // Student ID card export
  const idCardRef = useRef<HTMLDivElement | null>(null);
  const [idCardExportLoading, setIdCardExportLoading] = useState(false);

  // KYC Modal State
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [previewImage, setPreviewImage] = useState("");

  // Payment State
  const [activeEmiId, setActiveEmiId] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<{
    emiId: string | null;
    type: "error" | "success" | "warning" | null;
    message: string | null;
  }>({
    emiId: null,
    type: null,
    message: null,
  });

  const toast = useToast();

  useEffect(() => {
    const fetchAdmission = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}admission-payment/public/${admissionId}`,
        );
        setAdmission(res.data);
      } catch (err) {
        toast({
          title: "Access Denied",
          description: "Invalid secure link or the record has been moved.",
          status: "error",
          duration: 5000,
        });
      } finally {
        setTimeout(() => setLoading(false), 1500);
      }
    };
    if (admissionId) fetchAdmission();
  }, [admissionId, toast]);

  const clearError = () => {
    setPaymentStatus({ emiId: null, type: null, message: null });
    setIsProcessing(false);
  };

  // --- Handlers ---

  const handleDownload = (url: string, filename: string) => {
    // For images, we can open in new tab or try to force download
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAdmissionIdCardAsImage = async () => {
    try {
      if (!idCardRef.current) return;
      setIdCardExportLoading(true);

      const admissionCode =
        admission?.admissionId || admission?._id || "ADMISSION";

      const canvas = await html2canvas(idCardRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });

      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `Admission-ID-${admissionCode}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log("Failed to download ID card as image:", e);
    } finally {
      setIdCardExportLoading(false);
    }
  };

  const downloadAdmissionIdCardAsPdf = async () => {
    try {
      if (!idCardRef.current) return;
      setIdCardExportLoading(true);

      const admissionCode =
        admission?.admissionId || admission?._id || "ADMISSION";

      const element = idCardRef.current;

      const worker = html2pdf() as any;
      await worker
        .from(element)
        .set({
          margin: [0.2, 0.2, 0.2, 0.2],
          filename: `Admission-ID-${admissionCode}.pdf`,
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
          jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
        })
        .save();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log("Failed to download ID card as PDF:", e);
    } finally {
      setIdCardExportLoading(false);
    }
  };

  const handlePreview = (url: string) => {
    setPreviewImage(url);
    onOpen();
  };

  const createPaypalOrder = async () => {
    clearError();
    setIsProcessing(true);
    try {
      const res = await axios.post(
        `${BASE_URL}admission-payment/paypal/create`,
        {
          admissionId: admission._id,
          emiId: activeEmiId,
        },
      );
      return res.data.id;
    } catch (err: any) {
      setIsProcessing(false);
      setPaymentStatus({
        emiId: activeEmiId,
        type: "error",
        message: err.response?.data?.message || "PayPal Init Failed",
      });
      throw err;
    }
  };

  const onPaypalApprove = async (data: any) => {
    try {
      const res = await axios.post(
        `${BASE_URL}admission-payment/paypal/capture`,
        {
          admissionId: admission._id,
          emiId: activeEmiId,
          orderID: data.orderID,
        },
      );

      if (res.data.status === "success") {
        setPaymentStatus({
          emiId: activeEmiId,
          type: "success",
          message: "Payment Verified Successfully!",
        });
        setTimeout(() => window.location.reload(), 2000);
      }
    } catch (err: any) {
      setIsProcessing(false);
      setPaymentStatus({
        emiId: activeEmiId,
        type: "error",
        message: err.response?.data?.message || "Verification Failed",
      });
    }
  };

  const handleRazorpayPayment = async (amount: number, emiId: string) => {
    clearError();
    setActiveEmiId(emiId);
    setIsProcessing(true);

    const isLoaded = await loadRazorpayScript(
      "https://checkout.razorpay.com/v1/checkout.js",
    );
    if (!isLoaded) {
      setIsProcessing(false);
      setPaymentStatus({
        emiId,
        type: "error",
        message: "Security SDK Load Failed",
      });
      return;
    }

    try {
      const res = await axios.post(
        `${BASE_URL}admission-payment/razorpay/create`,
        {
          admissionId: admission._id,
          emiId: emiId,
        },
      );

      const orderData = res.data;
      const rzKey = orderData.keyId;
      if (!rzKey) {
        setIsProcessing(false);
        setPaymentStatus({
          emiId,
          type: "error",
          message: "Razorpay is not configured for this institute.",
        });
        return;
      }
      const options = {
        key: rzKey,
        amount: orderData.amount,
        currency: orderData.currency,
        name: admission?.parentId?.Name || "Admission Fees",
        description: `Installment Payment - ${admission.name}`,
        image: admission?.parentId?.ComapanyImageOne,
        order_id: orderData.id,
        handler: async function (response: any) {
          try {
            const verifyRes = await axios.post(
              `${BASE_URL}admission-payment/razorpay/verify`,
              {
                admissionId: admission._id,
                emiId: emiId,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
            );

            if (verifyRes.data.status === "success") {
              setPaymentStatus({
                emiId,
                type: "success",
                message: "Verified! Please wait...",
              });
              setTimeout(() => window.location.reload(), 2000);
            }
          } catch (err) {
            setIsProcessing(false);
            setPaymentStatus({
              emiId,
              type: "error",
              message: "Payment Verification Failed",
            });
          }
        },
        prefill: {
          name: admission.name,
          email: admission.email,
          contact: admission.mobileNo,
        },
        theme: { color: "#3182ce" },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
            setPaymentStatus({
              emiId,
              type: "warning",
              message: "Transaction was cancelled by user.",
            });
          },
        },
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    } catch (err: any) {
      setIsProcessing(false);
      setPaymentStatus({
        emiId,
        type: "error",
        message: "Could not initialize secure gateway.",
      });
    }
  };

  if (loading) {
    return (
      <Flex
        h="100vh"
        w="100vw"
        align="center"
        justify="center"
        bg="white"
        direction="column"
      >
        <Box animation={`${heartbeat} 1.5s infinite`} mb={6}>
          <Icon as={FaShieldAlt} color="blue.500" boxSize="80px" />
        </Box>
        <VStack spacing={2}>
          <Heading size="md" color="gray.700">
            Establishing Secure Connection
          </Heading>
          <HStack color="gray.500" fontSize="sm">
            <Spinner size="xs" />
            <Text>Verifying SSL Encryption...</Text>
          </HStack>
        </VStack>
        <Box w="200px" mt={8}>
          <Progress
            size="xs"
            isIndeterminate
            colorScheme="blue"
            borderRadius="full"
          />
        </Box>
      </Flex>
    );
  }

  if (!admission) return <Box p={10}>Admission Not Found</Box>;

  // Calculations
  const currency = admission.currency || "INR";
  const installDetails = admission.installDetails || [];
  const admissionCode = admission?.admissionId || admission?._id || "-";
  const formatBatchValue = (v: any) => {
    if (!v) return "N/A";
    if (typeof v === "string" && /^\d{2}\/\d{4}$/.test(v)) {
      // Already "MM/YYYY"
      return v;
    }
    if (typeof v === "string" && /^\d{4}-\d{2}$/.test(v)) {
      const [yyyy, mm] = v.split("-");
      return `${mm}/${yyyy}`;
    }
    return v;
  };
  const initials =
    admission?.name
      ?.split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((p: string) => p.charAt(0).toUpperCase())
      .join("") || "ST";

  // Calculate paid amount (installments + downpayment)
  const paidInstallments = installDetails.reduce(
    (acc: number, curr: any) => acc + (curr.isPaid ? curr.amount : 0),
    0,
  );
  // Only add downPayment if it exists and is part of total logic (Assuming yes based on prompt)
  const totalPaid = paidInstallments + (admission.downPayment || 0);

  const pendingAmount = admission.totalCoursePrice - totalPaid;
  const progressPercent = (totalPaid / admission.totalCoursePrice) * 100;

  // Find first unpaid EMI to open accordion
  const firstUnpaidIndex = installDetails.findIndex((emi: any) => !emi.isPaid);

  // KYC Data Map
  const kycDocuments = [
    { key: "profilePic", label: "Profile Picture" },
    { key: "frontAadhar", label: "Aadhar Front" },
    { key: "backAadhar", label: "Aadhar Back" },
    { key: "panCard", label: "PAN Card" },
  ];

  return (
    <Box minH="100vh" bg="gray.50" fontFamily="sans-serif">
      {/* Top Bar */}
      <Flex
        bg="blue.700"
        color="white"
        py={3}
        px={6}
        justify="center"
        align="center"
        shadow="md"
      >
        <HStack spacing={3}>
          <Icon as={FaLock} animation={`${pulse} 2s infinite`} />
          <Text fontSize="sm" fontWeight="bold" letterSpacing="wide">
            SECURE PAYMENT GATEWAY ACTIVE
          </Text>
        </HStack>
      </Flex>

      <Container maxW="container.xl" py={8}>
        {/* Header */}
        <Flex
          justify="space-between"
          align="center"
          mb={8}
          direction={{ base: "column", md: "row" }}
          gap={4}
        >
          <HStack spacing={4}>
            {/* Company Logo if available in parentId */}
            {admission.parentId?.ComapanyImageOne && (
              <Image
                src={admission.parentId.ComapanyImageOne}
                h="50px"
                objectFit="contain"
              />
            )}
            <VStack align="start" spacing={0}>
              <Heading size="lg" color="gray.800">
                {admission.parentId?.Name || "Admission Portal"}
              </Heading>
              <Text color="gray.500" fontSize="sm">
                Ref: {admission._id.slice(-8).toUpperCase()} | Date:{" "}
                {new Date(admission.admDate).toLocaleDateString()}
              </Text>
            </VStack>
          </HStack>

          <Badge
            colorScheme={
              admission.admStatus === "Approved"
                ? "green"
                : admission.admStatus === "Rejected"
                  ? "red"
                  : "orange"
            }
            variant="solid"
            px={4}
            py={2}
            borderRadius="full"
            fontSize="sm"
            textTransform="uppercase"
          >
            STATUS: {admission.admStatus || "Pending"}
          </Badge>
        </Flex>

        <Card
          variant="outline"
          boxShadow="xl"
          bg="white"
          borderRadius="2xl"
          overflow="hidden"
          border="none"
        >
          <Tabs isFitted variant="unstyled" colorScheme="blue" defaultIndex={0}>
            <TabList bg="gray.100" p={2}>
              <Tab
                borderRadius="lg"
                _selected={{ bg: "white", shadow: "sm", color: "blue.600" }}
                fontWeight="bold"
                fontSize="md"
              >
                <Icon as={FaFileAlt} mr={2} /> Summary Details
              </Tab>
              <Tab
                borderRadius="lg"
                _selected={{ bg: "white", shadow: "sm", color: "green.600" }}
                fontWeight="bold"
                fontSize="md"
              >
                <Icon as={FaMoneyBillWave} mr={2} /> Fees & Payments
              </Tab>
            </TabList>

            <TabPanels p={{ base: 3, md: 6 }}>
              {/* --- TAB 1: SUMMARY VIEW --- */}
              <TabPanel>
                <Grid
                  templateColumns={{ base: "1fr", lg: "repeat(3, 1fr)" }}
                  gap={6}
                >
                  {/* Column 1: Student Profile */}
                  <GridItem colSpan={{ base: 1, lg: 1 }}>
                    <Card variant="filled" bg="blue.50">
                      <CardBody>
                        <HStack mb={4} color="blue.700">
                          <Icon as={FaUser} boxSize={5} />
                          <Heading size="md">Student Profile</Heading>
                        </HStack>
                        <VStack align="stretch" spacing={4}>
                          <InfoRow label="Full Name" value={admission.name} />
                          <InfoRow label="Email" value={admission.email} />
                          <InfoRow label="Mobile" value={admission.mobileNo} />
                          <InfoRow
                            label="Alt Mobile"
                            value={admission.altMobileNo || "N/A"}
                          />
                          <InfoRow
                            label="Date of Birth"
                            value={
                              admission.dateOfBirth
                                ? new Date(admission.dateOfBirth).toLocaleDateString()
                                : "N/A"
                            }
                          />
                          <InfoRow
                            label="Father's Name"
                            value={admission.fathersName || "N/A"}
                          />
                          <Box>
                            <Text
                              fontSize="xs"
                              fontWeight="bold"
                              color="gray.500"
                              textTransform="uppercase"
                              mb={1}
                            >
                              ADDRESS
                            </Text>
                            <Text fontSize="sm" color="gray.800">
                              {admission.address || "N/A"}
                            </Text>
                          </Box>
                        </VStack>
                      </CardBody>
                    </Card>
                  </GridItem>

                  {/* Column 2: Academic Info */}
                  <GridItem colSpan={{ base: 1, lg: 1 }}>
                    <Card variant="filled" bg="purple.50">
                      <CardBody>
                        <HStack mb={4} color="purple.700">
                          <Icon as={FaUniversity} boxSize={5} />
                          <Heading size="md">Academic Info</Heading>
                        </HStack>
                        <VStack align="stretch" spacing={4}>
                          <InfoRow
                            label="Course Name"
                            value={admission.courseId?.CourseName || "N/A"}
                          />
                          <InfoRow
                            label="Branch"
                            value={admission.branchId?.BranchName || "N/A"}
                          />
                          <InfoRow
                            label="Counsellor"
                            value={admission.counsellorId?.Name || "N/A"}
                          />
                          <InfoRow
                            label="Enrollment No"
                            value={admission.enrollNo || "N/A"}
                          />
                          <InfoRow
                            label="Admission Date"
                            value={new Date(
                              admission.admDate,
                            ).toLocaleDateString()}
                          />
                          <InfoRow
                            label="Session / Batch"
                            value={`${formatBatchValue(
                              admission.sessionFrom,
                            )} - ${formatBatchValue(admission.sessionTo)}`}
                          />
                          <InfoRow
                            label="Highest Graduation"
                            value={admission.highestGraduation || "N/A"}
                          />
                        </VStack>
                      </CardBody>
                    </Card>
                  </GridItem>

                  {/* Column 3: KYC Documents */}
                  <GridItem colSpan={{ base: 1, lg: 1 }}>
                    <Card variant="outline" height="100%">
                      <CardBody>
                        <HStack mb={4} color="teal.600">
                          <Icon as={FaFileAlt} boxSize={5} />
                          <Heading size="md">KYC Documents</Heading>
                        </HStack>

                        <VStack align="stretch" spacing={3}>
                          {kycDocuments.map((doc) => {
                            const url = admission[doc.key];
                            if (!url) return null;
                            return (
                              <HStack
                                key={doc.key}
                                justify="space-between"
                                p={2}
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="md"
                              >
                                <HStack>
                                  <Image
                                    src={url}
                                    boxSize="40px"
                                    objectFit="cover"
                                    borderRadius="md"
                                  />
                                  <Text fontSize="sm" fontWeight="bold">
                                    {doc.label}
                                  </Text>
                                </HStack>
                                <HStack>
                                  <Tooltip label="Preview">
                                    <IconButton
                                      size="sm"
                                      icon={<FaEye />}
                                      aria-label="Preview"
                                      onClick={() => handlePreview(url)}
                                    />
                                  </Tooltip>
                                  <Tooltip label="Download">
                                    <IconButton
                                      size="sm"
                                      icon={<FaDownload />}
                                      aria-label="Download"
                                      onClick={() =>
                                        handleDownload(url, doc.label)
                                      }
                                    />
                                  </Tooltip>
                                </HStack>
                              </HStack>
                            );
                          })}
                          {kycDocuments.every((d) => !admission[d.key]) && (
                            <Text
                              fontSize="sm"
                              color="gray.500"
                              fontStyle="italic"
                            >
                              No documents uploaded.
                            </Text>
                          )}
                        </VStack>
                      </CardBody>
                    </Card>
                  </GridItem>
                </Grid>

                {/* Student ID Card */}
                <Card
                  variant="outline"
                  bg="white"
                  borderRadius="2xl"
                  boxShadow="xl"
                  border="none"
                  overflow="hidden"
                  mt={6}
                >
                  <Box
                    ref={idCardRef}
                    w="100%"
                    maxW={{ base: "520px", md: "680px" }}
                    style={{ aspectRatio: "4 / 3" }}
                    p={6}
                    bgGradient="linear-gradient(135deg, #0B5ED7 0%, #6D28D9 55%, #7C3AED 100%)"
                    color="white"
                    position="relative"
                    overflow="hidden"
                  >
                    <Box
                      position="absolute"
                      top="-90px"
                      right="-110px"
                      w="260px"
                      h="260px"
                      borderRadius="full"
                      bg="whiteAlpha.20"
                    />
                    <Box
                      position="absolute"
                      bottom="-120px"
                      left="-120px"
                      w="320px"
                      h="320px"
                      borderRadius="full"
                      bg="whiteAlpha.15"
                    />

                    {/* Card content */}
                    <Box position="relative" zIndex={1}>
                      {/* Top row */}
                      <Flex justify="space-between" align="flex-start" gap={4}>
                        <Box>
                          {admission.parentId?.ComapanyImageOne ? (
                            <Image
                              src={admission.parentId.ComapanyImageOne}
                              h="42px"
                              objectFit="contain"
                              alt="Company logo"
                            />
                          ) : (
                            <Text fontSize="sm" fontWeight="900" color="whiteAlpha.900">
                              {admission.parentId?.Name || "Institute"}
                            </Text>
                          )}
                          <Text
                            mt={2}
                            fontSize="xs"
                            color="whiteAlpha.850"
                            fontWeight="800"
                            letterSpacing="wide"
                          >
                            STUDENT ID CARD
                          </Text>
                        </Box>

                        <Badge
                          bg="whiteAlpha.18"
                          border="1px solid"
                          borderColor="whiteAlpha.28"
                          px={4}
                          py={2}
                          borderRadius="full"
                          fontSize="sm"
                          fontWeight="900"
                        >
                          {admissionCode}
                        </Badge>
                      </Flex>

                      {/* Middle row */}
                      <Flex
                        align="center"
                        gap={5}
                        mt={6}
                        flexDirection={{ base: "row", sm: "row" }}
                      >
                        {admission.profilePic ? (
                          <Image
                            src={admission.profilePic}
                            w="92px"
                            h="92px"
                            borderRadius="full"
                            objectFit="cover"
                            border="4px solid"
                            borderColor="whiteAlpha.40"
                            alt="Student profile"
                          />
                        ) : (
                          <Box
                            w="92px"
                            h="92px"
                            borderRadius="full"
                            bg="whiteAlpha.20"
                            border="4px solid"
                            borderColor="whiteAlpha.40"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            fontWeight="900"
                            fontSize="28px"
                          >
                            {initials}
                          </Box>
                        )}

                        <Box>
                          <Text fontSize="lg" fontWeight="950" lineHeight="1.2">
                            {admission.name}
                          </Text>
                          <Text color="whiteAlpha.900" fontSize="sm" mt={1}>
                            {admission.email}
                          </Text>
                          <Text color="whiteAlpha.900" fontSize="sm" mt={1}>
                            {admission.mobileNo}
                          </Text>
                          <Text color="whiteAlpha.800" fontSize="xs" mt={2}>
                            Enrolled:{" "}
                            {admission.admDate
                              ? new Date(admission.admDate).toLocaleDateString()
                              : "N/A"}
                          </Text>
                        </Box>
                      </Flex>

                      {/* Divider */}
                      <Divider borderColor="whiteAlpha.30" my={5} />

                      {/* Bottom info */}
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                        <Box>
                          <Text fontSize="xs" color="whiteAlpha.850" fontWeight="900">
                            Course
                          </Text>
                          <Text fontWeight="800">
                            {admission.courseId?.CourseName || "N/A"}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="whiteAlpha.850" fontWeight="900">
                            Branch
                          </Text>
                          <Text fontWeight="800">
                            {admission.branchId?.BranchName || "N/A"}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="whiteAlpha.850" fontWeight="900">
                            Counsellor
                          </Text>
                          <Text fontWeight="800">
                            {admission.counsellorId?.Name || "N/A"}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="whiteAlpha.850" fontWeight="900">
                            Payment
                          </Text>
                          <Text fontWeight="800">
                            {admission.paymentMode === "auto_paypal"
                              ? "PayPal"
                              : admission.paymentMode === "auto_razorpay"
                                ? "Razorpay"
                                : "Manual"}
                          </Text>
                        </Box>
                      </SimpleGrid>

                      {/* Footer */}
                      <Box
                        mt={5}
                        p={3}
                        bg="whiteAlpha.12"
                        border="1px solid"
                        borderColor="whiteAlpha.18"
                        borderRadius="xl"
                      >
                        <Text fontSize="10px" color="whiteAlpha.850" fontWeight="900" letterSpacing="wide">
                          SECURE ADMISSION PORTAL
                        </Text>
                        <Text fontSize="xs" color="whiteAlpha.900" mt={1} noOfLines={1}>
                          Pay securely from:{" "}
                          <Box as="span" fontWeight="900">
                            {`/admission-payment?admissionId=${admission._id}`}
                          </Box>
                        </Text>
                      </Box>
                    </Box>
                  </Box>

                  <Box p={4}>
                    <Flex
                      gap={3}
                      justify="flex-end"
                      wrap="wrap"
                    >
                      <Button
                        leftIcon={<FaDownload />}
                        onClick={downloadAdmissionIdCardAsImage}
                        isLoading={idCardExportLoading}
                        loadingText="Preparing..."
                        colorScheme="blue"
                        borderRadius="lg"
                        bg="linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)"
                        color="white"
                        _hover={{ transform: "translateY(-1px)" }}
                        transition="all 0.2s ease"
                      >
                        Download PNG
                      </Button>
                      <Button
                        leftIcon={<FaDownload />}
                        onClick={downloadAdmissionIdCardAsPdf}
                        isLoading={idCardExportLoading}
                        loadingText="Preparing..."
                        colorScheme="purple"
                        variant="outline"
                        borderRadius="lg"
                        borderColor="purple.200"
                        _hover={{ transform: "translateY(-1px)" }}
                        transition="all 0.2s ease"
                      >
                        Download PDF
                      </Button>
                    </Flex>
                    <Text mt={2} fontSize="xs" color="gray.500" textAlign="right">
                      Tip: downloads may take a few seconds depending on images.
                    </Text>
                  </Box>
                </Card>
              </TabPanel>

              {/* --- TAB 2: PAYMENTS --- */}
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  {/* Financial Summary */}
                  <Grid
                    templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                    gap={6}
                  >
                    <StatBox
                      label="Total Course Fee"
                      value={admission.totalCoursePrice}
                      currency={currency}
                      color="blue"
                      icon={FaChalkboardTeacher}
                    />
                    <StatBox
                      label="Total Paid"
                      value={totalPaid}
                      currency={currency}
                      color="green"
                      icon={FaCheckCircle}
                    />
                    <StatBox
                      label="Pending Balance"
                      value={pendingAmount}
                      currency={currency}
                      color="red"
                      icon={FaMoneyBillWave}
                    />
                  </Grid>

                  {/* Progress Bar */}
                  <Box>
                    <Flex justify="space-between" mb={1}>
                      <Text fontSize="sm" fontWeight="bold" color="gray.600">
                        Payment Progress
                      </Text>
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        color={
                          progressPercent === 100 ? "green.600" : "blue.600"
                        }
                      >
                        {progressPercent.toFixed(0)}% Completed
                      </Text>
                    </Flex>
                    <Progress
                      value={progressPercent}
                      colorScheme={progressPercent === 100 ? "green" : "blue"}
                      borderRadius="full"
                      hasStripe
                      isAnimated={progressPercent < 100}
                      h="12px"
                    />
                  </Box>

                  {/* Payment Mode Indicator */}
                  <Alert status="info" variant="left-accent" borderRadius="md">
                    <Icon as={FaCreditCard} mr={2} />
                    <Text fontSize="sm">
                      Payment Mode:{" "}
                      <b>
                        {admission.paymentMode === "auto_paypal"
                          ? "PayPal (International)"
                          : admission.paymentMode === "auto_razorpay"
                            ? "Razorpay (Domestic)"
                            : "Manual"}
                      </b>{" "}
                      | Type: <b>{admission.paymentType}</b>
                    </Text>
                  </Alert>

                  {/* Installments Accordion */}
                  <Card variant="outline">
                    <CardBody p={0}>
                      <Accordion
                        allowToggle
                        defaultIndex={
                          firstUnpaidIndex !== -1 ? [firstUnpaidIndex] : []
                        }
                      >
                        {/* 1. Down Payment (If exists and logic requires showing it separately, otherwise usually included in total) */}
                        {/* Note: Assuming Down Payment is handled as part of total calc or separate entry. 
                            If you want a specific row for Down Payment, you can render it here manually. 
                            For now, relying on installDetails array. */}

                        {installDetails.map((emi: any, index: number) => {
                          const isLocked =
                            !emi.isPaid &&
                            index !== firstUnpaidIndex &&
                            firstUnpaidIndex !== -1 &&
                            index > firstUnpaidIndex;
                          const isProcessingThis =
                            isProcessing && activeEmiId === emi._id;

                          return (
                            <AccordionItem
                              key={emi._id}
                              isDisabled={isLocked}
                              borderTop="none"
                              borderBottom="1px solid"
                              borderColor="gray.100"
                            >
                              <h2>
                                <AccordionButton
                                  _expanded={{
                                    bg: emi.isPaid ? "green.50" : "blue.50",
                                  }}
                                  py={4}
                                >
                                  <Box flex="1" textAlign="left">
                                    <HStack>
                                      {emi.isPaid ? (
                                        <Icon
                                          as={FaCheckCircle}
                                          color="green.500"
                                          boxSize={5}
                                        />
                                      ) : isLocked ? (
                                        <Icon
                                          as={FaLock}
                                          color="gray.400"
                                          boxSize={5}
                                        />
                                      ) : (
                                        <Icon
                                          as={FaInfoCircle}
                                          color="blue.500"
                                          boxSize={5}
                                        />
                                      )}

                                      <VStack align="start" spacing={0} ml={2}>
                                        <Text
                                          fontWeight="bold"
                                          fontSize="md"
                                          color="gray.700"
                                        >
                                          {emi.label ||
                                            `Installment ${index + 1}`}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                          Due Date:{" "}
                                          {new Date(
                                            emi.installDate,
                                          ).toLocaleDateString()}
                                        </Text>
                                      </VStack>
                                    </HStack>
                                  </Box>

                                  <HStack spacing={4}>
                                    <Text fontWeight="bold" fontSize="md">
                                      {currency} {emi.amount.toLocaleString()}
                                    </Text>
                                    <Badge
                                      colorScheme={
                                        emi.isPaid
                                          ? "green"
                                          : isLocked
                                            ? "gray"
                                            : "blue"
                                      }
                                      variant="subtle"
                                      px={2}
                                      py={1}
                                      borderRadius="full"
                                    >
                                      {emi.isPaid
                                        ? "PAID"
                                        : isLocked
                                          ? "LOCKED"
                                          : "PAY NOW"}
                                    </Badge>
                                    <AccordionIcon />
                                  </HStack>
                                </AccordionButton>
                              </h2>
                              <AccordionPanel pb={6} bg="white">
                                {emi.isPaid ? (
                                  <SimpleGrid
                                    columns={{ base: 1, md: 2 }}
                                    spacing={4}
                                    bg="green.50"
                                    p={4}
                                    borderRadius="md"
                                    border="1px dashed"
                                    borderColor="green.200"
                                  >
                                    <Box>
                                      <Text
                                        fontSize="xs"
                                        color="gray.500"
                                        fontWeight="bold"
                                      >
                                        TRANSACTION ID
                                      </Text>
                                      <Text
                                        fontSize="sm"
                                        fontFamily="monospace"
                                        fontWeight="bold"
                                      >
                                        {emi.transactionData?.transactionId ||
                                          "MANUAL_ENTRY"}
                                      </Text>
                                    </Box>
                                    <Box>
                                      <Text
                                        fontSize="xs"
                                        color="gray.500"
                                        fontWeight="bold"
                                      >
                                        PAYMENT DATE & TIME
                                      </Text>
                                      <Text fontSize="sm" fontWeight="bold">
                                        {emi.paidDate
                                          ? new Date(
                                              emi.paidDate,
                                            ).toLocaleString()
                                          : "N/A"}
                                      </Text>
                                    </Box>
                                  </SimpleGrid>
                                ) : (
                                  <VStack align="stretch" spacing={4} pt={2}>
                                    {/* Inline Error */}
                                    {paymentStatus.emiId === emi._id &&
                                      paymentStatus.message && (
                                        <Alert
                                          status={paymentStatus.type as any}
                                          borderRadius="md"
                                          variant="left-accent"
                                        >
                                          <AlertIcon />
                                          <Box flex="1">
                                            <AlertTitle fontSize="sm">
                                              {paymentStatus.type === "error"
                                                ? "Payment Failed"
                                                : "Info"}
                                            </AlertTitle>
                                            <AlertDescription fontSize="xs">
                                              {paymentStatus.message}
                                            </AlertDescription>
                                          </Box>
                                          <CloseButton
                                            size="sm"
                                            onClick={clearError}
                                          />
                                        </Alert>
                                      )}

                                    <Text fontSize="sm" color="gray.600">
                                      You are about to pay{" "}
                                      <b>
                                        {currency} {emi.amount}
                                      </b>{" "}
                                      for {emi.label}. Please select your
                                      preferred payment method below.
                                    </Text>

                                    <Box maxW="400px">
                                      {admission.paymentMode ===
                                        "auto_paypal" && (
                                        <Box>
                                          {activeEmiId === emi._id &&
                                          !paymentStatus.message ? (
                                            <PayPalScriptProvider
                                              options={{
                                                clientId:
                                                  admission?.paymentIntegration
                                                    ?.paypalClientId ||
                                                  PAYPAL_CLIENT_ID,
                                                currency: "USD",
                                              }}
                                            >
                                              <PayPalButtons
                                                style={{
                                                  layout: "vertical",
                                                  height: 45,
                                                }}
                                                createOrder={createPaypalOrder}
                                                onApprove={onPaypalApprove}
                                                onError={(err) =>
                                                  setPaymentStatus({
                                                    emiId: emi._id,
                                                    type: "error",
                                                    message:
                                                      "PayPal Connection Error",
                                                  })
                                                }
                                                onCancel={() => {
                                                  setIsProcessing(false);
                                                  setActiveEmiId(null);
                                                }}
                                              />
                                            </PayPalScriptProvider>
                                          ) : (
                                            <Button
                                              w="full"
                                              colorScheme="blue"
                                              size="lg"
                                              onClick={() => {
                                                setActiveEmiId(emi._id);
                                                clearError();
                                              }}
                                            >
                                              Pay with PayPal
                                            </Button>
                                          )}
                                        </Box>
                                      )}

                                      {admission.paymentMode ===
                                        "auto_razorpay" && (
                                        <Button
                                          w="full"
                                          colorScheme="blue"
                                          size="lg"
                                          leftIcon={<Icon as={FaLock} />}
                                          isLoading={isProcessingThis}
                                          loadingText="Connecting to Razorpay..."
                                          onClick={() =>
                                            handleRazorpayPayment(
                                              emi.amount,
                                              emi._id,
                                            )
                                          }
                                        >
                                          Pay with Razorpay
                                        </Button>
                                      )}

                                      {admission.paymentMode === "Manual" && (
                                        <Alert status="info" borderRadius="md">
                                          <AlertIcon />
                                          <Text fontSize="xs">
                                            Online payment is disabled. Please
                                            contact the center admin to pay via
                                            Cash/Bank Transfer.
                                          </Text>
                                        </Alert>
                                      )}
                                    </Box>
                                  </VStack>
                                )}
                              </AccordionPanel>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    </CardBody>
                  </Card>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Card>
      </Container>

      {/* KYC Preview Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent bg="transparent" boxShadow="none">
          <ModalCloseButton
            color="white"
            bg="blackAlpha.500"
            borderRadius="full"
          />
          <ModalBody p={0} display="flex" justifyContent="center">
            <Image
              src={previewImage}
              maxH="80vh"
              borderRadius="md"
              objectFit="contain"
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// --- Sub Components ---
const InfoRow = ({ label, value }: any) => (
  <Flex
    justify="space-between"
    borderBottom="1px dashed"
    borderColor="gray.200"
    pb={2}
  >
    <Text
      color="gray.500"
      fontSize="xs"
      fontWeight="bold"
      textTransform="uppercase"
    >
      {label}
    </Text>
    <Text color="gray.800" fontSize="sm" fontWeight="600">
      {value}
    </Text>
  </Flex>
);

const StatBox = ({ label, value, currency, color, icon }: any) => (
  <Card
    variant="filled"
    bg={`${color}.50`}
    border="1px solid"
    borderColor={`${color}.100`}
  >
    <CardBody
      p={4}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
    >
      <Box>
        <Text
          color={`${color}.600`}
          fontSize="xs"
          fontWeight="bold"
          mb={1}
          textTransform="uppercase"
        >
          {label}
        </Text>
        <Heading size="md" color={`${color}.800`}>
          {currency} {value.toLocaleString()}
        </Heading>
      </Box>
      <Box
        bg="white"
        p={2}
        borderRadius="full"
        color={`${color}.500`}
        shadow="sm"
      >
        <Icon as={icon} boxSize={5} />
      </Box>
    </CardBody>
  </Card>
);

export default AdmissionPaymentPortal;
